import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import { SHARED_RECORD_TYPES, buildSharingFormValues } from "utils/sharing";
import SharingRow from "components/sharing/SharingRow";
import { useGetNetworksQuery } from "apis/network";

const STATUS_OPTIONS = ["active", "suspended", "inactive"];
const STATUS_SELECT_OPTIONS = STATUS_OPTIONS.map((status) => ({
  label: status.charAt(0).toUpperCase() + status.slice(1),
  value: status,
}));
const TRUST_ACCOUNT_ARRANGEMENT_OPTIONS = [
  { label: "Provider trust", value: "providerTrust" },
  { label: "Own agency trust", value: "own" },
  { label: "Third-party trust", value: "thirdParty" },
];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputClassName = (error) =>
  `form-input border ${error ? "border-red-500" : "border-mediumgray"}`;

const getFieldError = (errors, path) =>
  path.split(".").reduce((accumulator, key) => accumulator?.[key], errors)
    ?.message;

const optionalEmail = (label) => (value) =>
  !value || EMAIL_PATTERN.test(value) || `${label} must be a valid email`;

// Older organizations predate `smsProvider`, so fall back to whichever set of
// credentials is actually stored on them.
// Only the selected provider's credentials go to the backend — sending the
// other provider's keys alongside them would store stale, unusable config.
// Name, slug, logos, colours, fonts, social links and the SMS account belong
// to the NETWORK. The assistant name is an optional organization override.
const buildDefaultValues = (initialData = {}) => ({
  networkId: initialData.networkId?._id || initialData.networkId || "",
  status: initialData.status || "active",
  aiAssistant: {
    name: initialData.aiAssistant?.name || "",
    picture: initialData.aiAssistant?.picture || "",
  },
  branding: {
    adCopyDisclaimer: initialData.branding?.adCopyDisclaimer || "",
  },
  operationalEmails: {
    settlements: initialData.operationalEmails?.settlements || "",
    concierge: initialData.operationalEmails?.concierge || "",
    keyEvents: initialData.operationalEmails?.keyEvents || "",
    accounts: initialData.operationalEmails?.accounts || "",
    admin: initialData.operationalEmails?.admin || "",
  },
  trustAccount: {
    useBeleefTrustAccount:
      initialData.trustAccount?.useBeleefTrustAccount || false,
    isEnabled: initialData.trustAccount?.isEnabled || false,
    arrangement: initialData.trustAccount?.arrangement ?? null,
    name: initialData.trustAccount?.name || "",
    bsb: initialData.trustAccount?.bsb || "",
    accountNumber: initialData.trustAccount?.accountNumber || "",
    abn: initialData.trustAccount?.abn || "",
    gst: initialData.trustAccount?.gst || "",
    address: initialData.trustAccount?.address || "",
    mobile: initialData.trustAccount?.mobile || "",
    email: initialData.trustAccount?.email || "",
    licenseNumber: initialData.trustAccount?.licenseNumber || "",
    signatoryName: initialData.trustAccount?.signatoryName || "",
    signature: initialData.trustAccount?.signature || "",
  },
  sharing: buildSharingFormValues(initialData.sharing),
});

const Section = ({ title, description, children }) => (
  <section className="space-y-4 border border-lightgray rounded-lg p-4">
    <div>
      <h5 className="text-base font-semibold text-black">{title}</h5>
      {description ? (
        <p className="text-sm text-darkgray mt-1">{description}</p>
      ) : null}
    </div>
    {children}
  </section>
);

const Field = ({ label, error, hint, children }) => (
  <div className="space-y-2">
    <label className="form-label">{label}</label>
    {children}
    {hint ? <p className="text-xs text-darkgray">{hint}</p> : null}
    {error ? <p className="form-error-message">{error}</p> : null}
  </div>
);

const InputField = ({ label, error, hint, inputProps }) => (
  <Field label={label} error={error} hint={hint}>
    <input {...inputProps} className={inputClassName(error)} />
  </Field>
);

const TextareaField = ({ label, error, hint, textareaProps }) => (
  <Field label={label} error={error} hint={hint}>
    <textarea {...textareaProps} className={inputClassName(error)} />
  </Field>
);

export default function OrganizationEdit({ initialData = {}, onSubmit }) {
  const { data: networks } = useGetNetworksQuery();
  const networkOptions = useMemo(
    () =>
      (networks || []).map((network) => ({
        value: network._id,
        label: network.slug ? `${network.name} (${network.slug})` : network.name,
      })),
    [networks],
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: buildDefaultValues(initialData),
  });

  const submitForm = (data) => {
    const payload = { ...data };

    if (!initialData?._id) {
      onSubmit(payload);
      return;
    }

    // Carry through the fields this form does not edit, so a save does not
    // blank them.
    onSubmit({
      compliance: initialData.compliance,
      toneOfVoice: initialData.toneOfVoice,
      templates: initialData.templates,
      brandAssets: initialData.brandAssets,
      companySubscription: initialData.companySubscription,
      ...payload,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6 p-2">
      <Section
        title="Basic Information"
        description="An organization trades under a network. Leave the assistant name blank to inherit the network identity, or set a name for this organization and every office under it."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label="Network *"
            error={getFieldError(errors, "networkId")}
            hint="Create the network first if it does not exist yet."
          >
            <Controller
              control={control}
              name="networkId"
              rules={{ required: "Network is required" }}
              render={({ field }) => (
                <SearchSelect
                  options={networkOptions}
                  value={
                    networkOptions.find(
                      (option) => option.value === field.value,
                    ) || null
                  }
                  onChange={(option) => field.onChange(option?.value || "")}
                  placeholder="Select network..."
                />
              )}
            />
          </Field>
          <Field label="Status" error={getFieldError(errors, "status")}>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <SearchSelect
                  options={STATUS_SELECT_OPTIONS}
                  value={
                    STATUS_SELECT_OPTIONS.find(
                      (option) => option.value === field.value,
                    ) || null
                  }
                  onChange={(option) => field.onChange(option?.value || "")}
                  placeholder="Select status..."
                  searchable={false}
                />
              )}
            />
          </Field>
        </div>
      </Section>

      <Section
        title="Assistant identity"
        description="Optional override for this organization. An office below it may set a more specific name."
      >
        <InputField
          label="Assistant name"
          hint="Leave blank to inherit the network name."
          error={getFieldError(errors, "aiAssistant.name")}
          inputProps={{
            type: "text",
            maxLength: 60,
            ...register("aiAssistant.name", {
              maxLength: {
                value: 60,
                message: "Assistant name must be 60 characters or fewer",
              },
            }),
          }}
        />
        <InputField
          label="Assistant photo URL"
          hint="Leave blank to inherit the network photo."
          error={getFieldError(errors, "aiAssistant.picture")}
          inputProps={{ type: "url", ...register("aiAssistant.picture") }}
        />
      </Section>



      <Section
        title="Share Across Offices"
        description="What this organization's offices can see of each other's records. Off keeps each office to its own data. Sharing is mutual — every office both contributes and sees. Anything the network already shares stays visible regardless of these."
      >
        <div className="border border-mediumgray rounded overflow-hidden">
          {SHARED_RECORD_TYPES.map((recordType) => (
            <Controller
              key={recordType.key}
              control={control}
              name={`sharing.${recordType.key}`}
              render={({ field }) => (
                <SharingRow
                  recordType={recordType}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          ))}
        </div>
      </Section>

      <Section
        title="Ad Copy Disclaimer"
        description="Appended to portal ad copy. The only branding an organization sets for itself — logos, colours and fonts are the network's."
      >
        <TextareaField
          label="Disclaimer"
          error={getFieldError(errors, "branding.adCopyDisclaimer")}
          hint="Leave blank to use the default Beleef disclaimer."
          textareaProps={{
            rows: 8,
            placeholder: "Leave blank for the default",
            ...register("branding.adCopyDisclaimer"),
          }}
        />
      </Section>

      <Section title="Operational Emails">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Settlements"
            error={getFieldError(errors, "operationalEmails.settlements")}
            inputProps={{
              type: "email",
              ...register("operationalEmails.settlements", {
                validate: optionalEmail("Settlements email"),
              }),
            }}
          />
          <InputField
            label="Concierge"
            error={getFieldError(errors, "operationalEmails.concierge")}
            inputProps={{
              type: "email",
              ...register("operationalEmails.concierge", {
                validate: optionalEmail("Concierge email"),
              }),
            }}
          />
          <InputField
            label="Key Events"
            error={getFieldError(errors, "operationalEmails.keyEvents")}
            inputProps={{
              type: "email",
              ...register("operationalEmails.keyEvents", {
                validate: optionalEmail("Key events email"),
              }),
            }}
          />
          <InputField
            label="Accounts"
            error={getFieldError(errors, "operationalEmails.accounts")}
            inputProps={{
              type: "email",
              ...register("operationalEmails.accounts", {
                validate: optionalEmail("Accounts email"),
              }),
            }}
          />
          <InputField
            label="Admin"
            error={getFieldError(errors, "operationalEmails.admin")}
            inputProps={{
              type: "email",
              ...register("operationalEmails.admin", {
                validate: optionalEmail("Admin email"),
              }),
            }}
          />
        </div>
      </Section>

      <Section title="Trust Account">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm font-medium text-black">
            <input
              type="checkbox"
              {...register("trustAccount.useBeleefTrustAccount")}
            />
            Use Beleef Trust Account
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-black">
            <input type="checkbox" {...register("trustAccount.isEnabled")} />
            Enable Trust Account
          </label>
          <Field
            label="Arrangement"
            error={getFieldError(errors, "trustAccount.arrangement")}
          >
            <Controller
              control={control}
              name="trustAccount.arrangement"
              render={({ field }) => (
                <SearchSelect
                  options={TRUST_ACCOUNT_ARRANGEMENT_OPTIONS}
                  value={
                    TRUST_ACCOUNT_ARRANGEMENT_OPTIONS.find(
                      (option) => option.value === field.value,
                    ) || null
                  }
                  onChange={(option) => field.onChange(option?.value || null)}
                  placeholder="Select arrangement..."
                  searchable={false}
                />
              )}
            />
          </Field>
          <InputField
            label="Account Name"
            error={getFieldError(errors, "trustAccount.name")}
            inputProps={{ type: "text", ...register("trustAccount.name") }}
          />
          <InputField
            label="BSB"
            error={getFieldError(errors, "trustAccount.bsb")}
            inputProps={{ type: "text", ...register("trustAccount.bsb") }}
          />
          <InputField
            label="Account Number"
            error={getFieldError(errors, "trustAccount.accountNumber")}
            inputProps={{
              type: "text",
              ...register("trustAccount.accountNumber"),
            }}
          />
          <InputField
            label="ABN"
            error={getFieldError(errors, "trustAccount.abn")}
            inputProps={{ type: "text", ...register("trustAccount.abn") }}
          />
          <InputField
            label="GST"
            error={getFieldError(errors, "trustAccount.gst")}
            inputProps={{ type: "text", ...register("trustAccount.gst") }}
          />
          <InputField
            label="Mobile"
            error={getFieldError(errors, "trustAccount.mobile")}
            inputProps={{
              type: "text",
              ...register("trustAccount.mobile"),
            }}
          />
          <InputField
            label="Email"
            error={getFieldError(errors, "trustAccount.email")}
            inputProps={{
              type: "email",
              ...register("trustAccount.email", {
                validate: optionalEmail("Trust account email"),
              }),
            }}
          />
          <InputField
            label="License Number"
            error={getFieldError(errors, "trustAccount.licenseNumber")}
            inputProps={{
              type: "text",
              ...register("trustAccount.licenseNumber"),
            }}
          />
          <InputField
            label="Signatory Name"
            error={getFieldError(errors, "trustAccount.signatoryName")}
            inputProps={{
              type: "text",
              ...register("trustAccount.signatoryName"),
            }}
          />
          <InputField
            label="Signature"
            error={getFieldError(errors, "trustAccount.signature")}
            hint="URL, file reference, or raw signature string depending on backend expectations."
            inputProps={{
              type: "text",
              ...register("trustAccount.signature"),
            }}
          />
        </div>
        <TextareaField
          label="Address"
          error={getFieldError(errors, "trustAccount.address")}
          textareaProps={{
            rows: 3,
            placeholder: "Trust account address",
            ...register("trustAccount.address"),
          }}
        />
      </Section>


      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" className="black-button" disabled={isSubmitting}>
          {isSubmitting
            ? "Uploading..."
            : initialData._id
              ? "Update Organization"
              : "Create Organization"}
        </Button>
      </div>
    </form>
  );
}
